import './DeviceProperties.scss';
import React, { useEffect, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';

import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { List, Map } from 'immutable';
import PropTypes from 'prop-types';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import EditableCell from 'src/component/UI/EditableCell';
import { clearDeviceModbusPropertiesLoading, fetchCommLoops, updateDevice } from 'src/module/device/action';
import { useSelector, useDispatch } from 'react-redux';
import Button from 'src/component/UI/Button';
import DeviceLink from 'src/component/UI/DeviceLink';
import { fetchIntegrations } from 'src/module/integration/action';
import { getIntegrations } from 'src/module/integration/selector';
import { celsiusToFahrenheitConvertor, fahrenheitToCelsiusConvertor, roundToNearestDecimal } from 'src/utils/utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { getCommLoops, getLoadingModbusProperties } from 'src/module/device/selector';
import { deviceTypeConfig } from 'src/utils/deviceTypeConfig';
import _ from 'lodash';
import EditIcon from '@mui/icons-material/EditSharp';
import LocationModal from './LocationModal';
import ConfirmModal from 'src/component/UI/ConfirmModal';
import toast from 'src/utils/toast';
import FeatureFence from 'src/component/FeatureFence';
import PermissionFence from 'src/component/PermissionFence';
import { isControllerLikeCircuit as isControllerLikeCircuitDevice } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import InfoBanner from 'src/component/UI/InfoBanner';

function Section ({ children, section, sectionKey }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <>
      <thead key={`${section}-${sectionKey}`}>
        <tr>
          <th className='expandable' colSpan={2} onClick={() => setCollapsed(!collapsed)}>{t(section)}  &nbsp;<span className='expand-arrow'>{collapsed ? '▲' : '▼'}</span></th>
        </tr>
      </thead>
      <tbody>
        {collapsed ? children : null}
      </tbody>
    </>
  );
}

export default function DeviceProperties (props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    device,
    permission,
    configFields,
    temperatureUnit
  } = props;
  const deviceConfig: any = deviceTypeConfig[device?.get('type')];
  const integrations = useSelector(getIntegrations);
  const loadingModbusProperties: any = useSelector(getLoadingModbusProperties(device?.get('id')));
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const [collapsed, setCollapsed] = useState(isMobile);
  const isControllerLikeCircuit = isControllerLikeCircuitDevice(device?.get('device_factory'));
  const isCM2000PDevice = device?.get('device_factory') === 'NVentCM2000P';
  const isGenesisController = device?.get('device_factory') === 'ThermonGenesisController';

  // clear loading modbus property on new device screen render
  useEffect(() => {
    if (device?.get('id')) {
      dispatch(clearDeviceModbusPropertiesLoading(device?.get('id')));
    }
  }, [device?.get('id')]);

  if (!isCM2000PDevice) {
    delete deviceTypeConfig['circuit']?.properties?.designedSettings?.atcMode;
  }

  if (isGenesisController) {
    if (deviceTypeConfig['controller']?.properties?.additionalData) {
      (deviceTypeConfig['controller'].properties.additionalData as any).isCelsius = {
        selector: ['is_celsius'], overrideElement: 'offlineSelector'
      };
    }
  }
  let frogName = null, frogId = null;
  const frogClients = [{ label: t('common.none'), value: 'null' }];
  integrations?.forEach(integration => {
    if (integration.get('type') === 'frog') {
      frogId = integration?.get('id');
      frogName = integration?.getIn(['settings', 'name']) || frogId.substring(0, 5);
      frogClients.push({ label: frogName, value: frogId });
    }
  });
  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  useEffect(() => {
    dispatch(fetchIntegrations());
    dispatch(fetchCommLoops());
  }, [dispatch]);

  const handleUpdateDeviceField = (deviceId: string, field: string | number) => {
    return (value) => {
      const rec: any = {};
      const cleanedValue = value === 'null' ? null : value;
      _.setWith(rec, field, cleanedValue, Object);
      dispatch(updateDevice(deviceId, Map(rec)));
    };
  };

  const handleUpdateDeviceNumericField = (deviceId: string, field: string) => {
    return (value) => {
      let cleanedValue;
      if (typeof value === 'string' && value?.includes('.')) {
        const floatValue = parseFloat(value);
        cleanedValue = Math.round(floatValue * 10) / 10;
      } else {
        cleanedValue = parseInt(value, 10);
      }

      if (isNaN(cleanedValue)) {
        cleanedValue = null;
        toast.error(t('deviceProperties.mustBeInteger', { field: t(field) }));
      }

      handleUpdateDeviceField(deviceId, field)(cleanedValue);
    };
  };

  const handleUpdateDeviceTemperatureField = (deviceId: string, field: string) => {
    return (value) => {
      let cleanedValue = roundToNearestDecimal(value, 1);
      if (field.includes('Temp') && temperatureUnit === 'F' && cleanedValue) {
        cleanedValue = fahrenheitToCelsiusConvertor(cleanedValue);
      }

      handleUpdateDeviceNumericField(deviceId, field)(cleanedValue);
    };
  };


  const formatHtcAddress = () => {
    const htcAddress = device?.get('device_address') || null;

    if (htcAddress !== null) {
      try {
        const deviceFactory = device?.get('device_factory');
        return deviceFactory === 'NVentNGC40' || deviceFactory === 'NVentNGC40IO' ? htcAddress.toString(16) : htcAddress;
      } catch {
        return htcAddress || 'N/A';
      }
    }

    return 'N/A';
  };

  const designedControlModes = configFields?.getIn(['settings', 'deviceOperatingStatus', 'options'],
    List(
      [
        { label: t('common.disabled'), value: 0 },
        { label: t('common.enabled'), value: 1 },
        { label: t('deviceProperties.forcedOn'), value: 2 },
      ]
    )).toJS();

  const designedRtdConfigOptions = configFields?.getIn(['settings', 'rtdConfig', 'options'],
    List()).toJS();
  const designedAtcModeOptions = configFields?.getIn(['settings', 'atcMode', 'options'], List()).toJS();

  const comms = useSelector(getCommLoops);
  const commLoops = [{ label: t('common.none'), value: 'null' }];
  comms?.forEach((loop: any) => {
    const id = loop?.get('id');
    const label = loop?.get('tag');
    commLoops.push({ label, value: id });
  });
  const readOnly = (val: string) => {
    return <>{device?.getIn(val, 'N/A') as string || 'N/A'}</>;
  };

  const PARITY_CHAR: Record<number, string> = { 0: 'N', 1: 'O', 2: 'E' };
  const serialFormat = () => {
    const dataBits = device?.get('data_bits');
    const parity = device?.get('parity');
    const stopBits = device?.get('stop_bits');
    if (dataBits == null && parity == null && stopBits == null) return <>N/A</>;
    const parityChar = PARITY_CHAR[parity as number] ?? parity ?? 'N';
    return <>{`${dataBits ?? '?'}${parityChar}${stopBits ?? '?'}`}</>;
  };
  // read only for circuit except for 920s / Elexant 4020i (controller-like circuits)
  const readOnlyCircuits = (val: string) => {
    if (isControllerLikeCircuit) {
      return <EditableCell
        onChange={handleUpdateDeviceField(device?.get('id'), val)}
        value={device?.getIn(val, null)}
        emptyCellLabel='N/A'
        permission={permission}
      />;
    }
    return <>{device?.getIn(val, 'N/A') as string || 'N/A'}</>;
  };
  const commLoopForCircuit = (val: Array<string>) => {
    if (isControllerLikeCircuit) {
      return selector(val, commLoops);
    }
    const currentId = device?.getIn(val, null);
    const match = commLoops.find(l => l.value === currentId);
    return <>{match ? match.label : 'N/A'}</>;
  };

  const tempReadOnly = (val: string) => {
    const value = temperatureUnit === 'F' && device?.getIn(val) ? celsiusToFahrenheitConvertor(device?.getIn(val)) : device?.getIn(val) || 'N/A';
    return <>{roundToNearestDecimal(value, 1)}</>;
  };

  const editTemperatureValueSelector = (val) => {
    const value = temperatureUnit === 'F' && device?.getIn(val) ? celsiusToFahrenheitConvertor(device?.getIn(val)) : device?.getIn(val) || null;
    return (
      <EditableCell
        id={`edit-temperature-id`}
        onChange={handleUpdateDeviceTemperatureField(device?.get('id') as string, val.join('.'))}
        value={value ? roundToNearestDecimal(value, 1) : null}
        emptyCellLabel={'N/A'}
        permission={permission}
      />
    );
  };

  const booleanOptions = [{ value: 0, label: t('common.no') }, { value: 1, label: t('common.yes') }];

  const triStateBooleanOptions = [
    { value: 'null', label: t('common.na') },
    { value: true, label: t('common.yes') },
    { value: false, label: t('common.no') }
  ];

  const triStateBooleanSelector = (val: Array<string>) => {
    const rawValue = device?.getIn(val);
    return (
      <EditableCell
        id={`tri-state-boolean-id`}
        onChange={handleUpdateDeviceField(device?.get('id') as string, val.join('.'))}
        value={rawValue === undefined || rawValue === null ? null : rawValue}
        emptyCellLabel='N/A'
        options={triStateBooleanOptions}
        permission={permission}
      />
    );
  };

  const booleanSelector = (val: Array<string>) => {
    return (
      <EditableCell
        id={`boolean-options-id`}
        onChange={handleUpdateDeviceField(device?.get('id') as string, val.join('.'))}
        value={device?.getIn(val) == null ? null : device?.getIn(val) && device?.getIn(val) !== 0 ? 1 : 0}
        emptyCellLabel={'N/A'}
        options={booleanOptions}
        permission={permission}
      />
    );
  };

  const selector = (val: Array<string>, options: any) => {
    return (
      <EditableCell
        id={`selector-id`}
        onChange={handleUpdateDeviceField(device?.get('id') as string, val.join('.'))}
        value={device?.getIn(val, null) !== null ? device?.getIn(val, null) : null}
        emptyCellLabel='N/A'
        options={options}
        permission={permission}
      />
    );
  };

  const numericSelector = (val: Array<string>, options: any) => {
    return (
      <EditableCell
        id={`selector-id`}
        onChange={handleUpdateDeviceNumericField(device?.get('id') as string, val.join('.'))}
        value={device?.getIn(val, null) !== null ? roundToNearestDecimal(device?.getIn(val, null), 1) : null}
        emptyCellLabel='N/A'
        options={options}
        permission={permission}
      />
    );
  };

  const offlineSelector = (val: Array<string>) => {
    return (
      <EditableCell
        id={`boolean-options-id`}
        onChange={handleUpdateDeviceField(device?.get('id') as string, val.join('.'))}
        value={device?.getIn(val) == null ? null : device?.getIn(val) && device?.getIn(val) !== '0' ? '1' : '0'}
        emptyCellLabel={'N/A'}
        options={booleanOptions}
        permission={'program-device'}
      />
    );
  };

  const deviceLink = (val: string) => {
    return (<DeviceLink label={device?.getIn(val, 'N/A')} controllerTag={device?.getIn(val, 'N/A')} />);
  };



  const [showLocationModal, setShowLocationModal] = useState(false);


  const googleLink = (val: Array<string>, permission?: string) => {
    const latitude = device?.get(val[0]);
    const longitude = device?.get(val[1]);
    const editIcon = (style) => (
      <PermissionFence can={permission || 'edit-device'} noPermissionComponent={null}>
        <div style={{ cursor: 'pointer', paddingLeft: '5px' }} onClick={() => setShowLocationModal(true)}>
          <EditIcon style={style} />
        </div>
      </PermissionFence>
    );
    if (latitude && longitude) {
      const url = `https://www.google.com/maps/@${latitude},${longitude}/?t=k&q=${latitude},${longitude}`;
      return <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
        <a href={url} target="_blank" rel="noopener noreferrer">{`${latitude}, ${longitude}`}</a>
        {editIcon({ fill: variables.greyLight })}
      </div >;
    }
    return <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>N/A
      {editIcon({ color: variables.greyLight })}
    </div>;
  };

  const overrideElements = {
    tempReadOnly,
    readOnly,
    readOnlyCircuits,
    commLoopForCircuit,
    formatHtcAddress,
    booleanSelector,
    triStateBooleanSelector,
    deviceLink,
    selector,
    googleLink,
    numericSelector,
    offlineSelector,
    editTemperatureValueSelector,
    serialFormat
  };
  const baudRateOptions = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200].map(rate => ({ label: String(rate), value: rate }));

  const parityOptions = [
    { label: t('commLoops.parityNone'), value: 0 },
    { label: t('commLoops.parityOdd'), value: 1 },
    { label: t('commLoops.parityEven'), value: 2 },
  ];

  const protocolOptions = [
    { label: t('commLoops.protocolRtu'), value: 1 },
    { label: t('commLoops.protocolAscii'), value: 2 },
  ];

  const selectorOptions = {
    frogClients,
    commLoops,
    designedControlModes,
    designedRtdConfigOptions,
    designedAtcModeOptions,
    parityOptions,
    baudRateOptions,
    protocolOptions
  };

  const overrideElementLogic = (element) => {
    if (element.overrideElement === 'selector') {
      return selector(element.selector, selectorOptions[element.options]);
    }
    if (element.overrideElement === 'googleLink') {
      return overrideElements[element.overrideElement](element.selector, permission);
    }
    return overrideElements[element.overrideElement](element.selector);
  };

  const IP_FIELDS = new Set(['ipAddress', 'port']);
  const SERIAL_FIELDS = new Set(['serialPort', 'baudRate', 'serialFormat']);
  const COMM_LOOP_SERIAL_FIELDS = new Set(['serialPort', 'baudRate', 'dataBits', 'parity', 'stopBits', 'protocol']);
  const isCommLoop = device?.get('type') === 'commLoop';

  const defaultCommConnType = (): 'tcp' | 'serial' => {
    const hasSerial = device?.get('serial_port') || device?.get('baud_rate') || device?.get('data_bits');
    const hasIp = device?.get('ip_address');
    if (hasSerial && !hasIp) return 'serial';
    return 'tcp';
  };

  const [commConnType, setCommConnType] = useState<'tcp' | 'serial'>(defaultCommConnType);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [undoBanner, setUndoBanner] = useState<{ message: string; restore: () => void } | null>(null);

  useEffect(() => {
    if (isCommLoop && !undoBanner) {
      setCommConnType(defaultCommConnType());
    }
  }, [device?.get('id'), device?.get('serial_port'), device?.get('baud_rate'), device?.get('ip_address')]);

  const connLabel = (type: 'tcp' | 'serial') =>
    type === 'tcp' ? t('commLoops.tcp') : t('commLoops.serialComPort');

  const handleConfirmSwitch = () => {
    const from = commConnType;
    const to: 'tcp' | 'serial' = from === 'tcp' ? 'serial' : 'tcp';
    const deviceId = device?.get('id');

    const snapshot: Record<string, any> = {};
    if (from === 'tcp') {
      snapshot.ip_address = device?.get('ip_address');
      snapshot.port = device?.get('port');
    } else {
      snapshot.serial_port = device?.get('serial_port');
      snapshot.baud_rate = device?.get('baud_rate');
      snapshot.data_bits = device?.get('data_bits');
      snapshot.parity = device?.get('parity');
      snapshot.stop_bits = device?.get('stop_bits');
    }

    const clearFields: Record<string, null> = {};
    Object.keys(snapshot).forEach(k => { clearFields[k] = null; });
    dispatch(updateDevice(deviceId, Map(clearFields)));

    setCommConnType(to);
    setShowSwitchDialog(false);
    setUndoBanner({
      message: t('commLoops.connectionTypeChanged', { type: connLabel(from) }),
      restore: () => {
        dispatch(updateDevice(deviceId, Map(snapshot)));
        setCommConnType(from);
        setUndoBanner(null);
      }
    });
  };

  const getHiddenConnFields = (sectionProps: Record<string, any>): Set<string> => {
    if (isCommLoop) {
      return commConnType === 'tcp' ? COMM_LOOP_SERIAL_FIELDS : IP_FIELDS;
    }
    const hasIp = IP_FIELDS.has.bind(IP_FIELDS);
    const hasSerial = SERIAL_FIELDS.has.bind(SERIAL_FIELDS);
    const ipFieldsPresent = Object.keys(sectionProps).some(k => hasIp(k));
    const serialFieldsPresent = Object.keys(sectionProps).some(k => hasSerial(k));
    if (!ipFieldsPresent || !serialFieldsPresent) return new Set();

    const ipHasValue = Object.keys(sectionProps).some(
      k => hasIp(k) && device?.getIn(sectionProps[k].selector)
    );
    const serialHasValue = device?.get('serial_port') || device?.get('baud_rate') || device?.get('data_bits');

    if (ipHasValue && !serialHasValue) return SERIAL_FIELDS;
    if (serialHasValue && !ipHasValue) return IP_FIELDS;
    // both or neither have values — hide serial group by default (IP takes precedence)
    return SERIAL_FIELDS;
  };

  const buildSection = (section, sectionKey) => {
    const sectionProps = deviceConfig?.properties[section] || {};
    const hiddenConnFields = section === 'properties' ? getHiddenConnFields(sectionProps) : new Set();
    const isCommLoopProps = isCommLoop && section === 'properties';
    const otherType: 'tcp' | 'serial' = commConnType === 'tcp' ? 'serial' : 'tcp';
    return (<Section key={sectionKey} section={section} sectionKey={{ sectionKey }}>
      {isCommLoopProps && (
        <tr className='conn-type-row'>
          <th>{t('commissioningCommLoop.networkSwitch.connectionType')}</th>
          <td className='device-property-value'>{connLabel(commConnType)}</td>
        </tr>
      )}
      {Object.keys(sectionProps).filter(element => !hiddenConnFields.has(element)).map((element, key) => {
        return (
          <FeatureFence key={`${sectionKey}-${key}`} can={deviceConfig?.properties[section][element].can}>
            <tr key={`${sectionKey}-${key}`} style={{ height: "39px" }}>
              <th>{isControllerLikeCircuit && element === 'controllerTag' ? t('commLoop') : t(element)}</th>
              <td className="device-property-value" data-testid={t(element).replace(/ /g, "-")}>
                {device?.getIn(['designed_values']) === undefined ? (
                  <Box display="flex" alignItems="center" justifyContent="flex-start">
                    <div>
                      <LoadingSpinner size={16} />
                    </div>

                  </Box>
                ) : (
                  deviceConfig?.properties[section][element].overrideElement ?
                    overrideElementLogic(deviceConfig?.properties[section][element])
                    :
                    <EditableCell
                      id={element.replace(/ /g, "-")}
                      onChange={handleUpdateDeviceField(device?.get('id'), deviceConfig?.properties[section][element].selector.join('.'))}
                      value={device?.getIn(deviceConfig?.properties[section][element].selector, null)}
                      emptyCellLabel='N/A'
                      permission={permission}
                      loading={loadingModbusProperties.getIn([deviceConfig?.properties[section][element]?.selector.join('') ?? '', 'loading'], false)}
                    />
                )}
              </td>
            </tr>
          </FeatureFence>
        );
      })}
      {isCommLoopProps && (
        <tr>
          <td colSpan={2} className='conn-switch-row'>
            <Button  onClick={() => setShowSwitchDialog(true)} link>
              {t('commLoops.switchConnectionTypeTo', { type: connLabel(otherType) })}
            </Button>
          </td>
        </tr>
      )}
    </Section>);
  };

  return (
    <Paper style={{ position: 'relative' }}>
      {isMobile ?
        <h3 className='expandable' onClick={() => setCollapsed(!collapsed)}>
          {t(deviceConfig?.title)}
          &nbsp;<span className='expand-arrow'>{collapsed ? '▲' : '▼'}</span>
        </h3>
        :
        <h3>
          {t(deviceConfig?.title)}
        </h3>
      }
      {device?.getIn(['designed_values']) === undefined && (
        <div className='loading-overlay-shadow-box'>
          <div className='loading-overlay-content'>
            <LogoSpinner size={200} />
          </div>
        </div>
      )}

      {!collapsed ? (
        <div id='device-properties' data-testid='device-properties'>
          {isCommLoop && undoBanner && (
            <InfoBanner variant='warning'>
              <span className='conn-undo-message'>{undoBanner.message}</span>
              <button className='conn-undo-btn' onClick={undoBanner.restore}>{t('common.undo')}</button>
            </InfoBanner>
          )}
          <table className='device-info'>

            {Object.keys(deviceConfig?.properties)?.map((section, sectionKey) => {
              return (deviceConfig?.sectionFeatureFence?.[section] ?
                <FeatureFence can={deviceConfig?.sectionFeatureFence?.[section]}>
                  {buildSection(section, sectionKey)}
                </FeatureFence>
                : buildSection(section, sectionKey));
            })}

          </table>
        </div>
      ) : null}
      {showLocationModal && <LocationModal deviceId={device.get('id')} latitude={device?.get('latitude')} longitude={device?.get('longitude')} onClose={() => setShowLocationModal(false)} />}
      <ConfirmModal
        open={showSwitchDialog}
        title={t('commLoops.switchDialogTitle', { type: connLabel(commConnType === 'tcp' ? 'serial' : 'tcp') })}
        message={t('commLoops.switchDialogBody', { current: connLabel(commConnType) })}
        handleConfirm={handleConfirmSwitch}
        handleReject={() => setShowSwitchDialog(false)}
        handleClose={() => setShowSwitchDialog(false)}
      />
    </Paper>
  );
}

DeviceProperties.propTypes = {
  permission: PropTypes.string,
  configFields: PropTypes.object,
  device: ImmutablePropTypes.map,
  temperatureUnit: PropTypes.string
};

Section.propTypes = {
  children: PropTypes.node,
  section: PropTypes.string.isRequired,
  sectionKey: PropTypes.any,
};

DeviceProperties.defaultProps = {
  permission: String,
  configFields: Map(),
  device: Map(),
  temperatureUnit: 'C'
};
